import type { StrokeTexture } from '../schema';

/**
 * SVG filter giving a freehand stroke a media texture — shared by the admin
 * teach stage and the playback renderer so a crayon stroke looks identical in
 * edit and in the classroom.
 *
 * - crayon: wavy displaced edge + coarse grain punched out of the ink.
 * - chalk:  finer, dustier grain with a softer edge.
 *
 * One filter instance per textured stroke (keyed by the stroke id) so seeds
 * differ subtly between strokes.
 */
export function strokeFilterId(strokeId: string): string {
  return `stroke-tex-${strokeId}`;
}

/** Deterministic small seed from the stroke id, so re-renders don't reshuffle. */
function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 997;
  return h + 1;
}

export function StrokeTextureFilter({
  strokeId,
  texture,
}: {
  strokeId: string;
  texture: StrokeTexture;
}) {
  const seed = seedOf(strokeId);
  const crayon = texture === 'crayon';
  return (
    <filter id={strokeFilterId(strokeId)} x="-20%" y="-20%" width="140%" height="140%">
      {/* Rough up the edge: low-frequency warp displaces the path sideways. */}
      <feTurbulence
        type="fractalNoise"
        baseFrequency={crayon ? '0.045 0.09' : '0.09 0.14'}
        numOctaves={2}
        seed={seed}
        result="warp"
      />
      <feDisplacementMap in="SourceGraphic" in2="warp" scale={crayon ? 5 : 3} result="rough" />
      {/* Grain: high-frequency noise turned into an alpha mask that punches
          pigment gaps out of the ink (denser for chalk's dusty look). */}
      <feTurbulence
        type="fractalNoise"
        baseFrequency={crayon ? 0.55 : 0.8}
        numOctaves={2}
        seed={seed + 13}
        result="grain"
      />
      <feColorMatrix in="grain" type="luminanceToAlpha" result="grainA" />
      <feComponentTransfer in="grainA" result="mask">
        <feFuncA
          type="gamma"
          amplitude={1}
          exponent={crayon ? 0.45 : 0.6}
          offset={crayon ? 0.42 : 0.3}
        />
      </feComponentTransfer>
      <feComposite in="rough" in2="mask" operator="in" />
    </filter>
  );
}
